import {Box, createStyles, Text} from '@mantine/core';
import React from 'react';

import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ContentsLayerScratchPlateViewer} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewer';
import useFilledScratchPlateData from 'notebook/integrations/plate-maps/scratch-plates/useFilledScratchPlateData';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {Header2} from 'ui-library/Headers';

import {MAX_PREVIEW_HEIGHT, MAX_PREVIEW_WIDTH} from './PopoverPreviewStyles';

function isValidDimension(value: number): boolean {
  return Number.isInteger(value) && value >= 1;
}

function getPlateShapeLabel(width: number, height: number): string {
  return `${width * height}-well plate (${height} × ${width})`;
}

export default function PlatePopoverContents({
  rawData,
}: {
  rawData: PipelineStepRawData<PipelineStepDataKind.PLATE>;
}): JSX.Element | null {
  const {width, height} = rawData;
  if (!isValidDimension(width) || !isValidDimension(height)) {
    return null;
  }
  return <PlatePreview rawData={rawData} width={width} height={height} />;
}

// Split out so useFilledScratchPlateData only hydrates once dimensions are known valid.
function PlatePreview({
  rawData,
  width,
  height,
}: {
  rawData: PipelineStepRawData<PipelineStepDataKind.PLATE>;
  width: number;
  height: number;
}): JSX.Element {
  const {classes} = useStyles();
  const {id, name} = rawData;
  const {result} = useFilledScratchPlateData(id);

  return (
    <Box>
      <div className={classes.header}>
        {name != null && name !== '' && <Header2>{name}</Header2>}
        <Text color="dimmed" size="sm">
          {getPlateShapeLabel(width, height)}
        </Text>
      </div>
      <Box className={classes.plate} p="md">
        <ContentsLayerScratchPlateViewer result={result} />
      </Box>
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  // Match the dataset popover's Pane header spacing: 12px top/right/left, 8px bottom, solid divider.
  header: {
    padding: `${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.sm}`,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
  },
  plate: {
    maxWidth: MAX_PREVIEW_WIDTH,
    maxHeight: MAX_PREVIEW_HEIGHT,
    overflow: 'auto',
  },
}));
